import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import useVelocityClient from '../../useVelocityClient';
import useCurrentUserAccount from '../../useCurrentUserAccount';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import {
	createOpenPerpNonMarketOrderIxs,
	MarketId,
	NonMarketOrderParamsConfig,
	OptionalAuctionParamsRequestInputs,
	UIMarket,
	UIOrderType,
} from '@velocity-exchange/common';
import {
	BN,
	VelocityClient,
	PostOnlyParams,
	PRICE_PRECISION_EXP,
	MAX_LEVERAGE_ORDER_SIZE,
} from '@velocity-exchange/sdk';
import { ORDER_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/orderPrep';
import { PerpTradeFormOutputProps } from 'src/stores/VelocityStore/actions/actionHelpers/types';
import Env, {
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import { safeCheckDevSwitchIsOn } from 'src/hooks/useDevSwitchIsOn';
import { TRADE_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/tradePrep';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useUserProtectionSlotClock } from 'src/hooks/useSlotClock';
import { generateUserOrderIdForUser } from 'src/utils/order';
import { useTxErrorHandler } from 'src/hooks/velocityActions/transactions/useTxErrorHandler';
import {
	runOrderActionToast,
	getOrderToastMessage,
} from 'src/hooks/velocityActions/transactions/runOrderActionToast';
import { buildScaledPerpLimitOrderIx } from 'src/utils/scaledOrders';
import { buildExpiredOrderCancelIx } from './buildExpiredOrderCancelIx';
import { TransactionInstruction } from '@solana/web3.js';
import { checkMaxLeverageFlagUsage } from 'src/utils/trade/checkMaxLeverageFlagUsage';
import { useAutoSignVelocityActionWithFallback } from 'src/hooks/wallets/useAutoSignVelocityActionWithFallback';
import { useWalletsV2 } from 'src/hooks/wallets/useWalletsV2';
import {
	UiEnhancedIWalletV2,
	TimingCallbacks,
} from 'src/utils/wallets/IWalletV2';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import { logNonMarketOrderTimingMetrics } from 'src/utils/logging/nonMarketOrderTiming';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

const getOrderConfig = (
	orderType: UIOrderType,
	marketId: MarketId,
	primaryPrice: BN,
	secondaryPrice: BN,
	oraclePrice: BN,
	postOnly: boolean,
	velocityClient: VelocityClient,
	bracketOrders: ReturnType<
		typeof ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps
	>['bracketOrders'],
	optionalAuctionParamsInputs: OptionalAuctionParamsRequestInputs
) => {
	let orderConfig: NonMarketOrderParamsConfig['orderConfig'];
	switch (orderType) {
		case 'limit': {
			const settings = syncGetCurrentSettings();
			const auctionStartPriceOffset =
				TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
					settings?.auctionStartPriceOffset as number | 'marketBased',
					marketId
				);
			const enablePlaceAndTake =
				!!settings?.placeAndTakeEnabled && velocityClient.txVersion === 0;

			orderConfig = {
				orderType: 'limit',
				limitPrice: primaryPrice,
				bracketOrders,
				limitAuction: {
					enable: !postOnly,
					dlobServerHttpUrl: Env.dlobServerHttpUrl,
					auctionStartPriceOffset,
					oraclePrice: oraclePrice,
					optionalLimitAuctionParams: optionalAuctionParamsInputs,
					usePlaceAndTake: {
						enable: enablePlaceAndTake,
						auctionDurationPercentage: Env.defaultAuctionDurationPercentage,
					},
				},
			};
			break;
		}
		case 'stopLimit': {
			orderConfig = {
				orderType: 'stopLoss',
				triggerPrice: primaryPrice,
				limitPrice: secondaryPrice,
			};
			break;
		}
		case 'stopMarket': {
			orderConfig = {
				orderType: 'stopLoss',
				triggerPrice: primaryPrice,
			};
			break;
		}
		case 'takeProfitLimit': {
			orderConfig = {
				orderType: 'takeProfit',
				triggerPrice: primaryPrice,
				limitPrice: secondaryPrice,
			};
			break;
		}
		case 'takeProfitMarket': {
			orderConfig = {
				orderType: 'takeProfit',
				triggerPrice: primaryPrice,
			};
			break;
		}
		case 'oracleLimit': {
			orderConfig = {
				orderType: 'oracleLimit',
				oraclePriceOffset: primaryPrice,
			};
			break;
		}
		default: {
			throw new Error(`Unsupported non-market order type: ${orderType}`);
		}
	}

	return orderConfig;
};

export const useOpenPerpNonMarketOrder = () => {
	const velocityClient = useVelocityClient();
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();
	const getOraclePrice = useGetOraclePriceForMarket();
	const handleTxError = useTxErrorHandler();
	const { captureEvent } = usePostHogCapture();
	const { slotDurationMs } = useUserProtectionSlotClock();
	const {
		authorityWallet,
		authorityWalletNoFeePayer,
		isEmbeddedWalletThePreferredTradingSigner,
	} = useWalletsV2();

	// Inner action that performs the actual order placement
	const openPerpNonMarketOrderAction = useCallback(
		async (
			signerWallet: UiEnhancedIWalletV2,
			isAutoSigned: boolean,
			toastId: string,
			perpTradeFormOutputProps: PerpTradeFormOutputProps & {
				orderType: Exclude<UIOrderType, 'market' | 'oracle'>;
				leadSide: 'base' | 'quote';
			}
		): Promise<string | undefined> => {
			invariant(velocityClient, 'driftClient is required');
			invariant(currentUserAccount?.client, 'currentUserAccount is required');
			const userClient = currentUserAccount.client;

			// Derive shared info from the trade form props (direction, size, bracket orders, etc)
			const {
				direction,
				baseAmountBigNum,
				bracketOrders,
				optionalAuctionParamsInputs,
			} = ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps(
				{
					...perpTradeFormOutputProps,
				} as PerpTradeFormOutputProps & { leadSide: 'base' | 'quote' },
				slotDurationMs,
				{
					isOracleOrder: false, // we don't use oracle order parameters for limit auction orders
				}
			);
			const orderType = perpTradeFormOutputProps.orderType ?? 'limit';

			const uiMarket = UIMarket.createPerpMarket(
				perpTradeFormOutputProps.targetMarketIndex
			);
			const oraclePrice = getOraclePrice(uiMarket.marketId);

			const primaryPriceBN: BN = parseInputBigNumOrZero(
				perpTradeFormOutputProps.priceBoxStringValue ?? '0',
				PRICE_PRECISION_EXP
			).val;
			const secondaryPriceBN: BN = parseInputBigNumOrZero(
				perpTradeFormOutputProps.secondaryPriceBoxStringValue ?? '0',
				PRICE_PRECISION_EXP
			).val;

			const userOrderId = generateUserOrderIdForUser(userClient);

			// Timing tracking for order execution
			const startTime = Date.now();
			const timingData = {
				ixCreationStartTime: null as number | null,
				ixCreationEndTime: null as number | null,
				txBuildingStartTime: null as number | null,
				txBuildingEndTime: null as number | null,
				feePayerSigningStartTime: null as number | null,
				feePayerSigningEndTime: null as number | null,
				embeddedWalletSigningStartTime: null as number | null,
				embeddedWalletSigningEndTime: null as number | null,
				sendTransactionStartTime: null as number | null,
				sendTransactionEndTime: null as number | null,
			};

			try {
				return await runOrderActionToast({
					toastId,
					isAutoSigned,
					pendingMessage: 'Placing order...',
					run: async () => {
						let ixs: TransactionInstruction[];

						timingData.ixCreationStartTime = Date.now();

						if (orderType === 'scaledOrders') {
							const scaledOrders = perpTradeFormOutputProps.scaledOrders;
							invariant(scaledOrders, 'scaledOrders is required');
							invariant(
								scaledOrders.sizeDistribution,
								'sizeDistribution is required'
							);
							invariant(scaledOrders.orderCount, 'orderCount is required');

							ixs = await buildScaledPerpLimitOrderIx({
								velocityClient,
								user: userClient,
								marketIndex: perpTradeFormOutputProps.targetMarketIndex,
								direction,
								totalBaseSize: baseAmountBigNum,
								orderCount: scaledOrders.orderCount,
								sizeDistribution: scaledOrders.sizeDistribution,
								startPrice: primaryPriceBN,
								endPrice: secondaryPriceBN,
								reduceOnly: perpTradeFormOutputProps.reduceOnly,
								postOnly: perpTradeFormOutputProps.postOnly,
								mainSignerOverride: signerWallet.publicKey,
								positionMaxLeverage: perpTradeFormOutputProps.displayLeverage,
								isolatedPositionDeposit:
									perpTradeFormOutputProps.isolatedPositionDeposit,
								additionalIsolatedPositionDeposits:
									perpTradeFormOutputProps.additionalIsolatedPositionDeposits,
							});

							if (safeCheckDevSwitchIsOn()) {
								console.log('[useOpenPerpNonMarketOrder] order params', {
									orderType,
									marketIndex: perpTradeFormOutputProps.targetMarketIndex,
									direction,
									totalBaseSize: baseAmountBigNum,
									orderCount: scaledOrders.orderCount,
									sizeDistribution: scaledOrders.sizeDistribution,
									startPrice: primaryPriceBN,
									endPrice: secondaryPriceBN,
									reduceOnly: perpTradeFormOutputProps.reduceOnly,
									postOnly: perpTradeFormOutputProps.postOnly,
								});
							}
						} else {
							const orderConfig = getOrderConfig(
								orderType,
								uiMarket.marketId,
								primaryPriceBN,
								secondaryPriceBN,
								oraclePrice.val,
								!!perpTradeFormOutputProps.postOnly,
								velocityClient,
								bracketOrders,
								optionalAuctionParamsInputs
							);

							// safety check for max leverage flag usage
							const useMaxLeverage = checkMaxLeverageFlagUsage(
								!!perpTradeFormOutputProps.maxLeverageSelected,
								perpTradeFormOutputProps.targetMarketIndex,
								baseAmountBigNum,
								oraclePrice,
								userClient,
								direction,
								perpTradeFormOutputProps.displayLeverage
							);
							const baseAmountToUseBN = useMaxLeverage
								? MAX_LEVERAGE_ORDER_SIZE
								: baseAmountBigNum.val;

							ixs = await createOpenPerpNonMarketOrderIxs({
								velocityClient: velocityClient,
								user: userClient,
								slotDuration: slotDurationMs,
								marketIndex: perpTradeFormOutputProps.targetMarketIndex,
								direction,
								amount: baseAmountToUseBN,
								assetType: 'base',
								orderConfig: orderConfig,
								reduceOnly: perpTradeFormOutputProps.reduceOnly,
								postOnly: perpTradeFormOutputProps.postOnly
									? PostOnlyParams.MUST_POST_ONLY
									: PostOnlyParams.NONE,
								userOrderId,
								mainSignerOverride: signerWallet.publicKey,
								positionMaxLeverage: perpTradeFormOutputProps.displayLeverage,
								isolatedPositionDepositsOverride:
									perpTradeFormOutputProps.isolatedPositionDeposit
										? {
												mainDeposit:
													perpTradeFormOutputProps.isolatedPositionDeposit,
												additionalDeposits:
													perpTradeFormOutputProps.additionalIsolatedPositionDeposits,
											}
										: undefined,
							});

							if (safeCheckDevSwitchIsOn()) {
								console.log('[useOpenPerpNonMarketOrder] order params', {
									orderType,
									marketIndex: perpTradeFormOutputProps.targetMarketIndex,
									direction,
									amount: baseAmountToUseBN,
									orderConfig,
									reduceOnly: perpTradeFormOutputProps.reduceOnly,
									postOnly: perpTradeFormOutputProps.postOnly,
									userOrderId,
								});
							}
						}

						timingData.ixCreationEndTime = Date.now();

						const timingCallbacks: TimingCallbacks = {
							onTxBuildingStart: () => {
								timingData.txBuildingStartTime = Date.now();
							},
							onTxBuildingEnd: () => {
								timingData.txBuildingEndTime = Date.now();
							},
							onFeePayerSigningStart: () => {
								timingData.feePayerSigningStartTime = Date.now();
							},
							onFeePayerSigningEnd: () => {
								timingData.feePayerSigningEndTime = Date.now();
							},
							onEmbeddedWalletSigningStart: () => {
								timingData.embeddedWalletSigningStartTime = Date.now();
							},
							onEmbeddedWalletSigningEnd: () => {
								timingData.embeddedWalletSigningEndTime = Date.now();
							},
							onSendTransactionStart: () => {
								timingData.sendTransactionStartTime = Date.now();
							},
							onSendTransactionEnd: () => {
								timingData.sendTransactionEndTime = Date.now();
							},
						};

						// If placing these order(s) would exceed the 32-order on-chain limit,
						// pair the cancellation of any expired orders first so their slots free up.
						const newOrderCount =
							orderType === 'scaledOrders'
								? (perpTradeFormOutputProps.scaledOrders?.orderCount ?? 1)
								: 1 + (Array.isArray(bracketOrders) ? bracketOrders.length : 0);
						const expiredCancelIx = await buildExpiredOrderCancelIx({
							velocityClient,
							user: userClient,
							newOrderCount,
							mainSignerOverride: signerWallet.publicKey,
						});
						const ixsToSend = expiredCancelIx ? [expiredCancelIx, ...ixs] : ixs;

						const txSig = await signerWallet.signAndSendTransaction(
							ixsToSend,
							getTxParams({
								marketId: uiMarket.marketId,
								computeUnits:
									orderType === 'scaledOrders' ? MAX_COMPUTE_UNITS : undefined,
							}),
							timingCallbacks
						);

						return txSig || undefined;
					},
					success: (sig) =>
						sig
							? {
									type: 'success',
									message: getOrderToastMessage({ orderType }),
								}
							: { type: 'error', message: 'Failed to open order.' },
					handleError: (e, id) => {
						// Transaction-size failures caused by isolated-position top-ups get a
						// bespoke, actionable message (a separate PR owns this special-case).
						const hasAdditionalDeposits =
							!!perpTradeFormOutputProps.additionalIsolatedPositionDeposits
								?.length;
						const isTxSizeError = (e as Error)?.message?.match(
							/\(max: encoded\/raw \d+\/\d+\)/
						);

						if (hasAdditionalDeposits && isTxSizeError) {
							handleTxError(
								new Error(
									'Transaction too large due to isolated position top-ups. Please manually add margin to your under-collateralized isolated positions or reduce their size before placing this order.'
								),
								id,
								'Transaction Too Large'
							);
							return;
						}

						handleTxError(e, id);
					},
					errorLogLabel: 'Error opening perp non-market order',
				});
			} finally {
				logNonMarketOrderTimingMetrics({
					startTime,
					timingData,
					marketIndex: perpTradeFormOutputProps.targetMarketIndex,
					orderType: orderType,
					baseAmount: baseAmountBigNum.toNum(),
					captureEvent,
				});
			}
		},
		[
			velocityClient,
			currentUserAccount?.client,
			getTxParams,
			getOraclePrice,
			handleTxError,
			captureEvent,
			slotDurationMs,
		]
	);

	const openPerpNonMarketOrderWithAutoSign =
		useAutoSignVelocityActionWithFallback(openPerpNonMarketOrderAction);

	// Final function that routes to authority wallet for isolated margin orders
	const openPerpNonMarketOrder = useCallback(
		async (
			toastId: string,
			perpTradeFormOutputProps: PerpTradeFormOutputProps & {
				orderType: Exclude<UIOrderType, 'market' | 'oracle'>;
				leadSide: 'base' | 'quote';
			}
		): Promise<string | undefined> => {
			// For isolated margin orders with embedded wallet, use authority wallet directly
			// because the Privy embedded wallet + fee payer flow doesn't work for isolated position deposits
			const needsAuthorityWalletFallback =
				isEmbeddedWalletThePreferredTradingSigner &&
				!!perpTradeFormOutputProps.isolatedPositionDeposit &&
				perpTradeFormOutputProps.orderType !== 'limit';

			if (needsAuthorityWalletFallback && authorityWallet) {
				return openPerpNonMarketOrderAction(
					authorityWallet,
					false, // isAutoSigned = false, user will manually sign
					toastId,
					perpTradeFormOutputProps
				);
			}

			// Scaled orders: authority pays, not fee payer service
			if (
				perpTradeFormOutputProps.orderType === 'scaledOrders' &&
				authorityWalletNoFeePayer
			) {
				return openPerpNonMarketOrderAction(
					authorityWalletNoFeePayer,
					false,
					toastId,
					perpTradeFormOutputProps
				);
			}

			return openPerpNonMarketOrderWithAutoSign(
				toastId,
				perpTradeFormOutputProps
			);
		},
		[
			openPerpNonMarketOrderAction,
			openPerpNonMarketOrderWithAutoSign,
			authorityWallet,
			authorityWalletNoFeePayer,
			isEmbeddedWalletThePreferredTradingSigner,
		]
	);

	return openPerpNonMarketOrder;
};
